import {
  ActivityPointRankExcludedDto,
  ActivityPointSeasonDto,
} from '@diritto/common/activityPoint/dtos/activity-point.dtos';
import { useMemo } from 'react';
import { configure } from 'utils/configure';
import { ActivityPointRankExcludedType } from '@diritto/common/activityPoint/constants';
import { SentryService } from 'utils/SentryService';

const Bronze3dImage = `${configure.staticPrefix}/images/quest/bronze3d.png`;
const Silver3dImage = `${configure.staticPrefix}/images/quest/silver3d.png`;
const Gold3dImage = `${configure.staticPrefix}/images/quest/gold3d.png`;

export const getExcludedRankText = (activityPointRankExcluded: ActivityPointRankExcludedDto) => {
  switch (activityPointRankExcluded.type) {
    case ActivityPointRankExcludedType.ByAdmin:
      return '⚠️ 현재 관리자에 의해 계정의 모든 프로필이 랭킹에서 제외되었어요.';
    case ActivityPointRankExcludedType.ByReport:
      return '⚠️ 누적된 신고로 자동으로 이용이 제재되어 계정의 모든 프로필이 랭킹에서 제외되었어요.';
    default:
      SentryService.captureMessage('[Not implemented] ActivityPointRankExcludedType', {
        extra: { activityPointRankExcluded },
      });
      return '⚠️ 현재 관리자에 의해 계정의 모든 프로필이 랭킹에서 제외되었어요.';
  }
};

type RewardDescType = {
  iconSrc: string;
  desc: string;
};
export const useActivityPointReward = (
  season: ActivityPointSeasonDto,
  rank?: number,
  activityPointRankExcluded?: ActivityPointRankExcludedDto,
) => {
  const rewardImages: string[] = useMemo(() => [Gold3dImage, Silver3dImage, Bronze3dImage], []);
  const { rewardPolicy, rankLimit } = season.extraInfo.payload;

  const rewardDescriptions = useMemo(() => {
    const result: RewardDescType[] = [];
    let moreThanOrEqualRank = 1;

    rewardPolicy.forEach((rp, index) => {
      result.push({
        iconSrc: rewardImages[index],
        desc: `${moreThanOrEqualRank}~${rp.lessThanOrEqualRank}등까지 ${rp.rewardDescription}`,
      });
      moreThanOrEqualRank = rp.lessThanOrEqualRank + 1;
    });

    return result;
  }, [rewardImages, rewardPolicy]);

  const reward = useMemo(() => {
    const today = new Date();

    if (activityPointRankExcluded) {
      switch (activityPointRankExcluded.type) {
        case ActivityPointRankExcludedType.ByAdmin:
          return '⚠️ 관리자에 의해 랭킹에서 제외됨';
        case ActivityPointRankExcludedType.ByReport:
          return '⚠️ 누적된 신고로 자동으로 이용이 제재됨';
        default:
          SentryService.captureMessage('[Not implemented] ActivityPointRankExcludedType', {
            extra: { activityPointRankExcluded },
          });
          return '⚠️ 관리자에 의해 랭킹에서 제외됨';
      }
    }

    if (rank === undefined) {
      return '집계되지 않음';
    }

    if (rank > rankLimit) {
      return '비 순위권';
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const index in rewardPolicy) {
      if (rank <= rewardPolicy[index].lessThanOrEqualRank) {
        if (today > new Date(season.endAt)) {
          return `${rewardPolicy[index].rewardDescription} 획득`;
        }
        return `${rewardPolicy[index].rewardDescription} 획득 가능`;
      }
    }

    return '집계되지 않음';
  }, [activityPointRankExcluded, rank, rankLimit, rewardPolicy, season.endAt]);

  const tierImage = useMemo(() => {
    if (rank === undefined || rank > rankLimit) {
      return undefined;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const index in rewardPolicy) {
      if (rank <= rewardPolicy[index].lessThanOrEqualRank) {
        return rewardImages[index];
      }
    }
    return undefined;
  }, [rank, rankLimit, rewardImages, rewardPolicy]);

  return { rewardDescriptions, reward, tierImage };
};
